import { FileDownloadOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import React, { useMemo, useState, useEffect, useContext } from 'react';

import { toast } from 'react-toastify';
import { getJustified, getJustifiedExport } from '../../api/approvalsApi';
import CWPContext from './useCWPContext';

export default function BelowTarget() {
  const { reloadTable, activeTab } = useContext(CWPContext);
  const [tableParams, setTableParams] = useState({
    rep_id: '',
    page: 0,
    sortModel: [],
    filterModel: { items: [] },
    page_size: 50,
    order_by: '',
    direction: '',
  });

  const [tableData, setTableData] = useState({
    data: [],
    totalRows: 0,
    loading: false,
  });

  const loadData = async (params) => {
    try {
      setTableData({ ...tableData, loading: true, data: [] });

      const res = await getJustified(params);

      const { success, message, data, totalRows } = res.data;

      if (!success) {
        throw Object.assign(new Error(message), { code: 402 });
      }

      setTableData({
        ...tableData,
        data,
        totalRows,
        loading: false,
      });
    } catch (error) {
      setTableData({
        ...tableData,
        data: [],
        totalRows: 0,
        loading: false,
      });
      toast.error(error.message);
    }
  };

  useEffect(() => {
    loadData(tableParams);
  }, []);

  useEffect(() => {
    if (reloadTable.below_target && activeTab === 2) {
      loadData(tableParams);
    }
  }, [reloadTable.below_target, activeTab]);

  const handlePage = (name, value) => {
    const newTableParams = { ...tableParams, [name]: value };
    setTableParams(newTableParams);
    loadData(newTableParams);
  };

  const handleFilterChange = (filterModel) => {
    const newTableParams = { ...tableParams, filterModel };

    setTableParams(newTableParams);
    loadData(newTableParams);
  };

  const handleSortChange = (params) => {
    let orderBy = '';
    let direction = '';

    params.forEach((row) => {
      if (row?.field) {
        orderBy = row?.field;
        direction = row?.sort;
      }
    });

    const newTableParams = { ...tableParams, order_by: orderBy, direction };

    setTableParams(newTableParams);
    loadData(newTableParams);
  };

  const handleExport = async () => {
    try {
      toast.loading('Please wait ...');

      const res = await getJustifiedExport(tableParams);

      const { success, result, message } = res.data;

      if (!success) {
        throw Object.assign(new Error(message), { code: 402 });
      }

      toast.dismiss();
      window.location.href = result;
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'med_rep_name',
        headerName: 'Specialist',
        flex: 1,
      },
      {
        field: 'timestamp',
        headerName: 'Submission Date',
        flex: 1,
        type: 'date',
        renderCell: ({ row }) => moment(row.timestamp).format('DD/MM/YYYY'),
      },
      {
        field: 'approval_id_date',
        headerName: 'Approval Date',
        flex: 1,
        type: 'date',
        renderCell: ({ row }) => moment(row.approval_id_date).format('DD/MM/YYYY'),
      },
      {
        field: 'md_name',
        headerName: 'MD Name',
        flex: 1,
      },
      {
        field: 'target_calls',
        headerName: 'Target Call',
        type: 'number',
        flex: 1,
      },
      {
        field: 'for_approval',
        headerName: 'CWP For Approval',
        type: 'number',
        flex: 1,
      },
      {
        field: 'approved',
        headerName: 'Approved Calls',
        type: 'number',
        flex: 1,
      },
      {
        field: 'total',
        headerName: 'Total',
        type: 'number',
        flex: 1,
        renderCell: ({ row }) => `${row.total}%`,
      },
      {
        field: 'justification',
        headerName: 'Reason/Justification',
        flex: 1,
      },
    ],
    [tableData.data],
  );

  return (
    <>
      <Box marginBottom={2} textAlign='right'>
        <Button
          variant='contained'
          color='primary'
          startIcon={<FileDownloadOutlined />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Box>
      <div style={{ height: '500px', marginBottom: '15px' }}>
        <DataGrid
          rows={tableData.data}
          rowCount={tableData.totalRows}
          columns={columns}
          loading={tableData.loading}
          pageSize={tableParams.page_size}
          disableSelectionOnClick
          paginationMode='server'
          onPageChange={(newPage) => handlePage('page', newPage)}
          onPageSizeChange={(newPageSize) => handlePage('page_size', newPageSize)}
          filterMode='server'
          onFilterModelChange={handleFilterChange}
          sortingMode='server'
          onSortModelChange={handleSortChange}
        />
      </div>
    </>
  );
}
